// src/components/pdf-editor/SidePanel.js
/**
 * Side Panel v2 – Properties editor for selected element
 */
import React from 'react';

const FONTS = [
  'Helvetica, Arial, sans-serif',
  'Times New Roman, Times, serif',
  'Courier New, Courier, monospace',
  'Georgia, serif',
  'Verdana, sans-serif',
];

const Label = ({ children }) => (
  <label style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase',
    letterSpacing: '0.05em', display: 'block', marginBottom: 4 }}>{children}</label>
);

const InputRow = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <Label>{label}</Label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '6px 8px', border: '1px solid rgba(148,163,184,0.35)',
  borderRadius: 6, fontSize: 13, color: '#e2e8f0', background: 'rgba(15,23,42,0.55)',
  outline: 'none', boxSizing: 'border-box',
};

const SidePanel = ({ selectedElement, onUpdateElement, onDeleteElement }) => {
  if (!selectedElement) {
    return (
      <div style={{
        width: 260, borderLeft: '1px solid rgba(34,211,238,0.2)', background: 'rgba(2,6,23,0.78)',
        backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, color: '#94a3b8', fontSize: 13, textAlign: 'center',
      }}>
        Select an element to edit its properties
      </div>
    );
  }

  const el = selectedElement;
  const style = el.style || {};
  const isText = el.type === 'text';
  const isImage = el.type === 'image';

  const updateStyle = (key, value) => {
    onUpdateElement({ ...el, style: { ...style, [key]: value } });
  };

  return (
    <div style={{
      width: 260, borderLeft: '1px solid rgba(34,211,238,0.2)', background: 'rgba(2,6,23,0.78)',
      backdropFilter: 'blur(10px)',
      overflowY: 'auto', padding: 16,
    }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 16px',
        color: '#e2e8f0', paddingBottom: 8, borderBottom: '1px solid rgba(148,163,184,0.2)' }}>
        {isText ? 'Text Properties' : 'Image Properties'}
      </h3>

      {/* ── Text properties ── */}
      {isText && (
        <>
          <InputRow label="Font Family">
            <select value={style.fontFamily || FONTS[0]} style={inputStyle}
              onChange={e => updateStyle('fontFamily', e.target.value)}>
              {FONTS.map(f => <option key={f} value={f}>{f.split(',')[0]}</option>)}
            </select>
          </InputRow>

          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <Label>Size</Label>
              <input type="number" value={style.fontSize || 12} min={4} max={200} style={inputStyle}
                onChange={e => updateStyle('fontSize', Math.max(4, +e.target.value))} />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Color</Label>
              <input type="color" value={style.color || '#000000'}
                style={{ ...inputStyle, padding: 2, height: 32, cursor: 'pointer' }}
                onChange={e => updateStyle('color', e.target.value)} />
            </div>
          </div>

          <InputRow label="Style">
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { key: 'fontWeight', val: 'bold', label: 'B', style: { fontWeight: 'bold' } },
                { key: 'fontStyle', val: 'italic', label: 'I', style: { fontStyle: 'italic' } },
              ].map(({ key, val, label, style: s }) => (
                <button key={key}
                  onClick={() => updateStyle(key, style[key] === val ? 'normal' : val)}
                  style={{
                    ...inputStyle, width: 36, padding: 6, textAlign: 'center',
                    cursor: 'pointer', ...s,
                    background: style[key] === val ? 'rgba(14,165,233,0.26)' : 'rgba(15,23,42,0.55)',
                    color: style[key] === val ? '#67e8f9' : '#cbd5e1',
                    borderColor: style[key] === val ? 'rgba(34,211,238,0.65)' : 'rgba(148,163,184,0.35)',
                  }}>{label}</button>
              ))}
            </div>
          </InputRow>

          <InputRow label="Alignment">
            <div style={{ display: 'flex', gap: 4 }}>
              {['left', 'center', 'right'].map(a => (
                <button key={a}
                  onClick={() => updateStyle('textAlign', a)}
                  style={{
                    ...inputStyle, flex: 1, padding: 6, textAlign: 'center', cursor: 'pointer',
                    fontSize: 12, textTransform: 'capitalize',
                    background: style.textAlign === a ? 'rgba(14,165,233,0.26)' : 'rgba(15,23,42,0.55)',
                    color: style.textAlign === a ? '#67e8f9' : '#cbd5e1',
                    borderColor: style.textAlign === a ? 'rgba(34,211,238,0.65)' : 'rgba(148,163,184,0.35)',
                  }}>{a}</button>
              ))}
            </div>
          </InputRow>

          <InputRow label="Text Content">
            <textarea value={el.text || ''} rows={3}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.4 }}
              onChange={e => onUpdateElement({ ...el, text: e.target.value, committed: false })} />
          </InputRow>
        </>
      )}

      {/* ── Image properties ── */}
      {isImage && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <Label>Width (pt)</Label>
              <input type="number" value={Math.round(el.w)} min={10} style={inputStyle}
                onChange={e => onUpdateElement({ ...el, w: Math.max(10, +e.target.value) })} />
            </div>
            <div style={{ flex: 1 }}>
              <Label>Height (pt)</Label>
              <input type="number" value={Math.round(el.h)} min={10} style={inputStyle}
                onChange={e => onUpdateElement({ ...el, h: Math.max(10, +e.target.value) })} />
            </div>
          </div>

          <InputRow label="Opacity">
            <input type="range" min={0} max={1} step={0.05} value={style.opacity ?? 1}
              style={{ width: '100%' }}
              onChange={e => updateStyle('opacity', +e.target.value)} />
            <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right' }}>
              {Math.round((style.opacity ?? 1) * 100)}%
            </div>
          </InputRow>
        </>
      )}

      {/* ── Position ── */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <Label>X</Label>
          <input type="number" value={Math.round(el.x)} style={inputStyle}
            onChange={e => onUpdateElement({ ...el, x: +e.target.value })} />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Y</Label>
          <input type="number" value={Math.round(el.y)} style={inputStyle}
            onChange={e => onUpdateElement({ ...el, y: +e.target.value })} />
        </div>
      </div>

      {/* ── Info ── */}
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 16, padding: '8px 0',
        borderTop: '1px solid rgba(148,163,184,0.2)' }}>
        <div>Page: {(el.pageIndex || 0) + 1}</div>
        <div>Origin: {el.origin === 'pdf' ? 'Extracted from PDF' : 'User added'}</div>
        {el.kind === 'signature' && <div>Type: Signature</div>}
      </div>

      {/* ── Delete ── */}
      <button onClick={onDeleteElement}
        style={{
          width: '100%', padding: '8px 0', borderRadius: 8,
          border: '1px solid rgba(248,113,113,0.45)', background: 'rgba(127,29,29,0.25)', color: '#fca5a5',
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          transition: 'all 0.15s',
        }}>
        Delete Element
      </button>
    </div>
  );
};

export default SidePanel;
